import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Search, Plus, Upload, Trash2, Link2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtBytes } from "@/lib/webApi";
import { useMe, PageHead, Empty, primary, ghost } from "@/pages/web/WebApp";
import { FileTable } from "@/pages/web/WebFiles";

export function StorageBar({ storage }) {
  const pct = storage?.cap ? Math.min(100, ((storage?.used || 0) / storage.cap) * 100) : 0;
  return (
    <div data-testid="web-storage-bar">
      <div className="h-1.5 w-full overflow-hidden rounded bg-white/5"><div className="h-full bg-amber-300 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <p className="mt-1 text-xs text-slate-500">{fmtBytes(storage?.used)} {storage?.cap ? `of ${fmtBytes(storage.cap)} used` : "used · unlimited"}</p>
    </div>
  );
}

export function Overview() {
  const { me } = useMe();
  const [data, setData] = useState({ recent: [], matters: [] });
  const nav = useNavigate();
  useEffect(() => {
    webApi.get("/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const newMatter = async () => {
    const name = window.prompt("Matter name");
    if (!name) return;
    try {
      const { data: m } = await webApi.post("/matters", { name });
      nav(`/app/matters/${m.id}`);
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div data-testid="web-overview">
      <PageHead title={me.name ? `Hello, ${me.name.split(" ")[0]}` : "Overview"}>
        <Link to="/app/upload" className={primary} data-testid="overview-upload-button"><Upload className="h-4 w-4" /> Upload</Link>
        <button onClick={newMatter} className={ghost} data-testid="overview-new-matter-button"><Plus className="h-4 w-4" /> New matter</button>
      </PageHead>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-white/10 bg-[#0E121A] p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Plan</p>
          <p className="mt-1 text-lg font-semibold text-slate-100" data-testid="overview-plan">{me.plan_label}</p>
          <div className="mt-3"><StorageBar storage={me.storage} /></div>
        </div>
        <div className="rounded-lg border border-white/10 bg-[#0E121A] p-4 md:col-span-2">
          <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Recent matters</p>
          {data.matters.length === 0 ? <p className="text-sm text-slate-500">No matters yet. Forward a court document on WhatsApp or upload here.</p> : (
            <div className="flex flex-wrap gap-2">
              {data.matters.map((m) => (
                <Link key={m.id} to={`/app/matters/${m.id}`} data-testid={`overview-matter-${m.id}`} className="rounded-md border border-white/10 px-3 py-1.5 text-sm text-slate-300 transition-colors duration-200 hover:border-amber-300/40">
                  {m.name} <span className="text-slate-500">· {m.files}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      {!me.store_on && <p className="mt-4 text-xs text-slate-500" data-testid="store-off-note">Your file store isn't switched on in this environment yet.</p>}
      <h2 className="mb-3 mt-8 text-base font-semibold text-slate-200 md:text-lg">Recent files</h2>
      {data.recent.length ? <FileTable rows={data.recent} /> : <Empty>No files yet. Forward one on WhatsApp or upload here.</Empty>}
    </div>
  );
}

export function Matters() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => webApi.get("/matters", { params: { q } }).then((r) => setRows(r.data)).catch(() => setRows([])), 200);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const k = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); document.getElementById("matter-search")?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div data-testid="web-matters">
      <PageHead title="Matters" sub="Press / to search" />
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
        <input id="matter-search" data-testid="matter-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search matters"
          className="w-full rounded-md border border-white/10 bg-[#0E121A] py-2.5 pl-9 pr-3 text-sm outline-none focus:border-amber-300/50" />
      </div>
      {rows && rows.length === 0 && <Empty>No matters yet. Forward a court document on WhatsApp and Dicta will suggest one.</Empty>}
      <div className="divide-y divide-white/5 rounded-lg border border-white/10">
        {(rows || []).map((m) => (
          <Link key={m.id} to={`/app/matters/${m.id}`} data-testid={`matter-row-${m.id}`} className="flex items-center justify-between px-4 py-3 transition-colors duration-200 hover:bg-white/[0.03]">
            <div>
              <p className="text-slate-100">{m.name}</p>
              {m.linked.length > 0 && <p className="text-xs text-slate-500">Linked: {m.linked.map((l) => `${l.name} (${l.kind})`).join(", ")}</p>}
            </div>
            <span className="text-sm text-slate-500">{m.files} file{m.files === 1 ? "" : "s"}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function LinkMatter({ id, onDone }) {
  const [all, setAll] = useState([]);
  const [other, setOther] = useState("");
  const [kind, setKind] = useState("connected");
  useEffect(() => {
    webApi.get("/matters").then((r) => setAll(r.data.filter((m) => m.id !== id)));
  }, [id]);
  const save = async () => {
    if (!other) return;
    try { await webApi.post(`/matters/${id}/links`, { other_id: other, kind }); toast.success("Linked"); onDone(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="link-matter-form">
      <select value={other} onChange={(e) => setOther(e.target.value)} data-testid="link-matter-select" className="rounded-md border border-white/10 bg-[#0E121A] px-2 py-2 text-sm">
        <option value="">Choose a matter</option>
        {all.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="link-kind-select" className="rounded-md border border-white/10 bg-[#0E121A] px-2 py-2 text-sm">
        {["appeal", "bail", "interim", "connected", "other"].map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <button onClick={save} className={primary} data-testid="link-matter-save">Link</button>
    </div>
  );
}

export function MatterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [linking, setLinking] = useState(false);
  const load = useCallback(() => webApi.get(`/matters/${id}`).then((r) => setM(r.data)).catch(() => nav("/app/matters")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!m) return null;
  const rename = async () => {
    const name = window.prompt("Rename matter", m.name);
    if (!name) return;
    await webApi.patch(`/matters/${id}`, { name });
    load();
  };
  const del = async () => {
    if (!window.confirm(`Delete "${m.name}"? ${m.delete_plan.erase} file(s) will be erased; ${m.delete_plan.stay} stay because they're also in another matter or the Library.`)) return;
    const { data } = await webApi.delete(`/matters/${id}`);
    toast.success(`Deleted. ${data.erased} erased, ${data.kept} kept.`);
    nav("/app/matters");
  };
  const unlink = async (o) => { await webApi.delete(`/matters/${id}/links/${o}`); load(); };
  return (
    <div data-testid="web-matter-detail">
      <PageHead title={m.name}>
        <button onClick={rename} className={ghost} data-testid="matter-rename-button"><Pencil className="h-4 w-4" /> Rename</button>
        <button onClick={() => setLinking(!linking)} className={ghost} data-testid="matter-link-button"><Link2 className="h-4 w-4" /> Link</button>
        <button onClick={del} className={`${ghost} text-red-300`} data-testid="matter-delete-button"><Trash2 className="h-4 w-4" /> Delete</button>
      </PageHead>
      {m.linked.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="matter-linked-list">
          {m.linked.map((l) => (
            <span key={l.id} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs text-slate-300">
              <Link to={`/app/matters/${l.id}`} className="hover:underline">{l.name}</Link> <span className="text-slate-500">({l.kind})</span>
              <button onClick={() => unlink(l.id)} data-testid={`matter-unlink-${l.id}`} className="ml-1 text-slate-500 hover:text-red-300"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {linking && <LinkMatter id={id} onDone={() => { setLinking(false); load(); }} />}
      <div className="mt-4">{m.files.length ? <FileTable rows={m.files} onChange={load} matterId={id} /> : <Empty>No files in this matter yet. Forward one on WhatsApp or upload here.</Empty>}</div>
      <p className="mt-4 text-xs text-slate-500">{m.files.length} file{m.files.length === 1 ? "" : "s"}</p>
    </div>
  );
}
